<template>
  <div class="code-verify">
    <div class="description">
      {{ description }}
    </div>

    <form-input-code
      ref="codeInput"
      v-model:otp-code="completeCode"
      :code-length="CODE_LENGTH"
      :hint="errorHint"
    />

    <div class="code-verify__resend">
      <span
        v-if="limitError"
        class="code-verify__resend-hint"
      >
        {{ getContent(fieldsSettings, defaultLocaleFieldsSettings, 'forms.verification.limitError') }}
      </span>

      <template v-if="!limitError">
        <div
          v-if="timer"
          class="code-verify__resend-timer"
        >
          <div class="code-verify__resend-label">
            {{ getContent(fieldsSettings, defaultLocaleFieldsSettings, 'forms.verification.timerLabel') }}
          </div>

          <div class="code-verify__resend-time">
            <span>{{ Math.floor(currentTime / 60) }}</span>:<span>{{ String(currentTime % 60).padStart(2, '0') }}</span>
          </div>
        </div>

        <div
          v-else
          class="resend"
          @click="resendCode"
        >
          <span>{{ getContent(fieldsSettings, defaultLocaleFieldsSettings, 'forms.verification.resendText') }}</span>
          <span class="link">
            {{ getContent(fieldsSettings, defaultLocaleFieldsSettings, 'forms.verification.resendLink') }}</span>
        </div>

        <button-base
          type="tertiary"
          size="xs"
          @click="pasteCode"
        >
          {{ getContent(layoutData, defaultLocaleLayoutData, 'buttons.paste') }}
        </button-base>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IChangeFormData, IChangePasswordRequest } from '@skeleton/core/types';

const { showAlert } = useLayoutStore();

const props = defineProps<{
  contactDetail: string;
  reason?: 'registration' | 'phoneVerification' | 'changingPass' | 'emailVerification';
  type?: 'email' | 'phone';
  passFormData?: IChangeFormData;
}>();

const emit = defineEmits([
  'update:isVerifyDisabled',
  'update:isLoading',
  'errorSendCode',
  'codeVerified',
]);
const {
  layoutData,
  defaultLocaleLayoutData,
  alertsData,
  defaultLocaleAlertsData,
  fieldsSettings,
  defaultLocaleFieldsSettings,
} = storeToRefs(useGlobalStore());

const { getContent } = useProjectMethods();
const {
  sendOtp,
  sendOtpAuth,
  phoneVerification,
  emailOtpCodeVerification,
  sendEmailVerifyCode,
  sendEmailOTPCodeChangePass,
} = useCoreAuthApi();
const { changeProfilePassword } = useCoreProfileApi();

const profileStore = useProfileStore();
const { profile } = storeToRefs(profileStore);

const completeCode = ref('');
const isLoading = ref(false);
const CODE_LENGTH = 6;

const description = computed(() => {
  const contentPath = props.type === 'email' ? 'forms.emailVerificationDescription' : 'forms.phoneVerificationDescription';
  const prefix = props.type === 'email' ? '' : '+';

  return getContent(fieldsSettings.value, defaultLocaleFieldsSettings.value, contentPath) + ` ${prefix}` + props.contactDetail;
});

const isVerifyDisabled = computed(() => {
  return !completeCode.value || errorHint.value || isLoading.value || limitError.value;
});

watch(isVerifyDisabled, () => {
  emit('update:isVerifyDisabled', isVerifyDisabled.value);
});

watch(isLoading, () => {
  emit('update:isLoading', isLoading.value);
});

watch(completeCode, () => {
  limitError.value = false;
  errorHint.value = undefined;

  if (!!completeCode.value && completeCode.value.length === CODE_LENGTH) {
    verifyCode();
  }
});

const TIMER_TIME = 60;
const timer = ref<any>(undefined);
const currentTime = ref<number>(TIMER_TIME);
const codeInput = ref(null);
const errorHint = ref<Maybe<{ variant: string; message: string }>>(undefined);

const startTimer = (): void => {
  clearInterval(timer.value);

  timer.value = setInterval(() => {
    if (currentTime.value === 0) {
      clearInterval(timer.value);
      timer.value = undefined;
      return;
    }
    currentTime.value -= 1;
  }, 1000);
};

const limitError = ref(false);

const resendCode = async (): Promise<void> => {
  codeInput.value?.reset();
  completeCode.value = '';
  limitError.value = false;
  errorHint.value = undefined;

  await sendVerifyCode();
};
const sendVerifyCode = async (): Promise<void> => {
  if (props.type === 'email' && props.reason === 'changingPass') {
    await sendChangePassEmailOTP();
  }
  else if (props.type === 'email' && props.reason === 'emailVerification') {
    await sendEmailCode();
  }
  else if (props.type === 'phone') {
    await sendSMSCode();
  }
};

const sendSMSCode = async (): Promise<void> => {
  if (isLoading.value) return;
  isLoading.value = true;

  try {
    const otpData = { phone: props.contactDetail, reason: props.reason };

    if (profile?.value) {
      await sendOtpAuth(otpData);
    }
    else {
      await sendOtp(otpData);
    }
    currentTime.value = TIMER_TIME;
    startTimer();
  }
  catch (error: any) {
    if (error.data?.error?.code === 11005) {
      showAlert(
        getContent(alertsData.value, defaultLocaleAlertsData.value, 'profile.otpLimitReached'),
        'error',
      );
      limitError.value = true;
    }
    else {
      showAlert({ title: error.data?.error?.message || 'Cannot send verification code' }, 'error');
    }

    emit('errorSendCode');
  }

  isLoading.value = false;
};

const sendEmailCode = async () => {
  try {
    await sendEmailVerifyCode({ email: props.contactDetail });

    showAlert(
      getContent(alertsData.value, defaultLocaleAlertsData.value, 'profile.sentCodeVerify'),
      'success',
    );
  }
  catch (error: any) {
    showAlert({ title: error.data?.error?.message || 'Cannot send verification code' }, 'error');
    emit('errorSendCode');
  }
};

const sendChangePassEmailOTP = async () => {
  try {
    if (isLoading.value) return;
    isLoading.value = true;

    await sendEmailOTPCodeChangePass();
    showAlert(
      getContent(alertsData.value, defaultLocaleAlertsData.value, 'profile.sentCodeVerify'),
      'success',
    );
  }
  catch (error: any) {
    if (error.response?.status === 400 && error.response?._data.error?.message) {
      showAlert(
        {
          title: 'Error',
          description: error.response?._data.error?.message,
        },
        'error',
      );
    }
  }
  finally {
    isLoading.value = false;
  }
};

const changePassword = async (): Promise<void> => {
  if (isLoading.value) return;
  isLoading.value = true;

  const changePassFormData = {
    ...props.passFormData,
    otp: completeCode.value,
  };

  try {
    isLoading.value = true;
    await changeProfilePassword(changePassFormData as IChangePasswordRequest);
    showAlert(
      getContent(
        alertsData.value,
        defaultLocaleAlertsData.value,
        'profile.passwordChanged',
      ),
      'success',
    );
    emit('codeVerified', { status: true });
  }
  catch (error: any) {
    if (error.response?.status === 422) {
      showAlert(
        getContent(alertsData.value, defaultLocaleAlertsData.value, 'profile.invalidPassword'),
        'error',
      );
    }
    else {
      showAlert(
        getContent(alertsData.value, defaultLocaleAlertsData.value, 'profile.codeEnteredIncorrectly'),
        'error',
      );
    }
  }
  finally {
    isLoading.value = false;
  }
};

const verifyPhone = async (): Promise<void> => {
  if (isLoading.value) return;
  isLoading.value = true;

  try {
    const result = await phoneVerification({
      phone: props.contactDetail,
      reason: props.reason,
      code: completeCode.value,
    });

    if (props.reason === 'phoneVerification') {
      await profileStore.getProfileData();
      useEvent('profileUpdated');

      showAlert(
        getContent(alertsData.value, defaultLocaleAlertsData.value, 'profile.phoneVerified'),
        'success',
      );
    }

    emit('codeVerified', result);
  }
  catch (error: any) {
    if (error.data?.error?.code === 11003) {
      errorHint.value = {
        variant: 'error',
        message: getContent(fieldsSettings.value, defaultLocaleFieldsSettings.value, 'forms.verification.invalidError'),
      };
    }
    else {
      errorHint.value = {
        variant: 'error',
        message: error.data?.error?.message || 'Cannot verify phone number',
      };
    }
  }

  isLoading.value = false;
};

const verifyEmail = async (): Promise<void> => {
  if (isLoading.value) return;
  isLoading.value = true;

  try {
    const result = await emailOtpCodeVerification({
      email: props.contactDetail,
      code: completeCode.value,
    });

    await profileStore.getProfileData();
    useEvent('profileUpdated');
    showAlert(
      getContent(alertsData.value, defaultLocaleAlertsData.value, 'profile.successAddEmail'),
      'success',
    );

    emit('codeVerified', result);
  }
  catch (error: any) {
    let message = '';

    if (error.data?.error?.code === 11003) {
      message = getContent(
        fieldsSettings?.value,
        defaultLocaleFieldsSettings?.value,
        'forms.verification.invalidError',
      );
    }
    else {
      message
          = error.data?.error?.message
            || getContent(
              fieldsSettings?.value,
              defaultLocaleFieldsSettings?.value,
              'forms.verification.cantVerifyEmailCode',
            );
    }

    if (error.status === 429) {
      showAlert(
        getContent(
          alertsData?.value,
          defaultLocaleAlertsData?.value,
          'profile.tooManyAttempts',
        ),
        'error',
      );
    }

    errorHint.value = {
      variant: 'error',
      message,
    };
  }
  isLoading.value = false;
};

const verifyCode = async (): Promise<void> => {
  if (props.type === 'email' && props.reason === 'changingPass') {
    await changePassword();
  }
  else if (props.type === 'email' && props.reason === 'emailVerification') {
    await verifyEmail();
  }
  else if (props.type === 'phone') {
    await verifyPhone();
  }
};

const pasteCode = async (): Promise<void> => {
  if (isLoading.value) return;
  const clipboardText = await navigator.clipboard.readText() as string;

  codeInput.value?.reset();
  completeCode.value = clipboardText.replace(/\D/g, '').slice(0, CODE_LENGTH);
};

defineExpose({
  sendVerifyCode,
  verifyCode,
});

onMounted(() => {
  startTimer();
});

onBeforeUnmount(() => {
  clearInterval(timer.value);
});
</script>

<style src="~/assets/styles/components/form/code-verify.scss" lang="scss" />
